// src/components/SquadManagePanel.js
// ============================================================================
// SQUAD MANAGE PANEL - Team Streams v2 (TEAM-BACKED squad streaming).
//
// v2 (founder 2026-08-04): a squad IS the user's registered Team. There is
// no separate squad roster - membership is exactly teams/{teamId}.memberIds,
// and the roster is managed ONLY from the Your Team tab (invite code /
// remove / transfer / disband). This panel carries the STREAMING controls
// only, and the SAME implementation renders in three places:
//   - StreamsScreen: inside the squad modal chrome (title/scroll/Close)
//   - StreamingAccountsScreen (Streaming Hub): inline "Squad Streams" section
//   - YourTeam: "Squad Streaming" section on the team card (pass
//     hideTeamLink - the user is already on the Team tab)
//
// Contains: the account-level squad-streaming opt-in toggle, the team's
// squad-stream card (captain: Start / Squad on-off / End; every member:
// Link up / Open), and a READ-ONLY roster with live/linked badges. All
// callable-backed; names come from public_profiles ONLY (canonical public
// identity).
//
// Props:
//   navigation      - react-navigation prop; used to reach the Your Team tab
//   onNavigate(id)  - called when the user taps Open on the squad; the parent
//                     decides how to get to the TeamStream page (StreamsScreen
//                     closes its modal first; the hub just navigates)
//   onSquadsChanged - OPTIONAL; called after actions that change what the
//                     public browse surface shows (link toggle, end squad) so
//                     StreamsScreen can refresh its listActiveTeamStreams
//                     cards immediately (it otherwise polls every 45s)
//   hideTeamLink    - OPTIONAL; suppress the "manage roster in Your Team"
//                     links when the panel is rendered ON the Your Team tab
//
// Also exports useTeamStreamsVisible(): the settings/streams.teamStreamsEnabled
// feature flag + users/{uid}.role === 'admin' exemption, as one hook.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Modal,
  Pressable,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { Ionicons } from '@expo/vector-icons';
import { db } from '../../firebaseConfig';
import { useError } from './ErrorContext';
import { SQUAD_OPT_IN_FIELD } from '../constants/teamStreams';

const COLORS = {
  BG: '#0b0b0b',
  CARD: '#111',
  CARD_BORDER: '#222',
  GOLD: '#FCD000',
  TEXT: '#EDEDED',
  DIM: '#888',
  LIVE: '#e02222',
};

// ----------------------------------------------------------------------------
// useTeamStreamsVisible - Team Streams visibility flag.
// Feature flag lives on settings/streams (world-readable feature-flag doc);
// admins are exempt from the flag (matches the backend callable gates) so
// squads can be exercised in prod while the feature ships dark.
// ----------------------------------------------------------------------------
export function useTeamStreamsVisible() {
  const [teamFlagOn, setTeamFlagOn] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const isLoggedIn = !!getAuth().currentUser;

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'settings', 'streams'),
      (snap) => setTeamFlagOn(!!(snap.exists() && snap.data().teamStreamsEnabled)),
      () => setTeamFlagOn(false)
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) { setIsAdminUser(false); return; }
    getDoc(doc(db, 'users', uid))
      .then((snap) => setIsAdminUser(!!(snap.exists() && snap.data()?.role === 'admin')))
      .catch(() => setIsAdminUser(false));
  }, [isLoggedIn]);

  return teamFlagOn || isAdminUser;
}

// ----------------------------------------------------------------------------
// Link-change bus: SquadLinkPrompt answers change linkedUids server-side, and
// any mounted SquadManagePanel (Streams modal, Hub, Your Team) must refetch
// or its Linked/Link up toggle goes stale behind the modal. Module-level so
// the prompt and the panels need no shared parent. (Review finding
// 2026-08-04.)
// ----------------------------------------------------------------------------
const squadLinkListeners = new Set();
const notifySquadLinkChanged = () => {
  squadLinkListeners.forEach((fn) => { try { fn(); } catch (_) { /* listener owns its errors */ } });
};

// ----------------------------------------------------------------------------
// SquadManagePanel
// ----------------------------------------------------------------------------
export default function SquadManagePanel({ navigation, onNavigate, onSquadsChanged, hideTeamLink }) {
  const { showError } = useError();
  const functions = getFunctions();

  const [team, setTeam] = useState(null);   // { teamId, name, tag, isCaptain, memberCount }
  const [squad, setSquad] = useState(null); // { teamStreamId, linkActive, myLinked, linkedCount, ... }
  const [roster, setRoster] = useState([]); // [{ uid, userName, isCaptain, linked, live }]
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  // Account-level squad opt-in (founder 2026-07-21): starting squad streaming
  // and linking into the split both require this switch ON.
  const [squadOptIn, setSquadOptIn] = useState(false);

  // Live-sync the opt-in toggle with the caller's users doc. The panel only
  // mounts when the squad UI is visible (flag on or admin), so no extra gate
  // is needed here - unmount tears the listener down.
  useEffect(() => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return undefined;
    const unsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      setSquadOptIn((snap.data() || {})[SQUAD_OPT_IN_FIELD] === true);
    }, () => {});
    return () => unsub();
  }, []);

  const toggleSquadOptIn = async () => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    try {
      await updateDoc(doc(db, 'users', uid), { [SQUAD_OPT_IN_FIELD]: !squadOptIn });
    } catch (err) {
      showError('Could not update the setting', 'Please try again.');
    }
  };

  const fetchSquadState = useCallback(async () => {
    try {
      setLoading(true);
      const fn = httpsCallable(functions, 'listMyTeamStreams');
      const result = await fn();
      setTeam(result.data?.team || null);
      setSquad(result.data?.squad || null);
      setRoster(Array.isArray(result.data?.roster) ? result.data.roster : []);
    } catch (err) {
      showError('Could not load your squad', err.message || 'Please try again.');
    } finally {
      setLoading(false);
    }
  }, [functions, showError]);

  // Fresh data every mount. StreamsScreen mounts the panel each time the
  // squad modal opens (unmounting discards state). Signed-out mounts skip
  // the fetch: listMyTeamStreams asserts auth, and a guaranteed error toast
  // on mount is worse than an empty panel (hosts gate the panel on auth,
  // this is defense in depth).
  useEffect(() => {
    if (!getAuth().currentUser) return;
    fetchSquadState();
  }, [fetchSquadState]);

  // Refetch when a SquadLinkPrompt answer changes link state elsewhere on
  // this screen - otherwise the Linked/Link up toggle behind the modal goes
  // stale until the next panel action.
  useEffect(() => {
    const onLinkChanged = () => { if (getAuth().currentUser) fetchSquadState(); };
    squadLinkListeners.add(onLinkChanged);
    return () => squadLinkListeners.delete(onLinkChanged);
  }, [fetchSquadState]);

  const squadErr = (err, fallback) =>
    String(err?.message || '').replace(/^.*?:/, '').trim() || fallback;

  const goToYourTeam = () => {
    if (navigation?.navigate) navigation.navigate('YourTeam');
  };

  // Captain: turn squad streaming ON for the team (the squad = the team).
  const startSquadStreaming = async () => {
    if (busy) return;
    if (!squadOptIn) {
      showError('Turn on squad streaming first', 'Flip the Squad streaming switch at the top of this panel, then start your squad.');
      return;
    }
    setBusy(true);
    try {
      await httpsCallable(functions, 'createTeamStream')({});
      fetchSquadState();
      if (onSquadsChanged) onSquadsChanged();
    } catch (err) {
      showError('Could not start squad streaming', squadErr(err, 'Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  // MY per-member link (founder 2026-07-21): linking is default OFF and each
  // member links themselves; only linked AND live members share squad Super
  // Crits.
  const toggleMyLink = async () => {
    if (busy || !squad) return;
    if (!squad.myLinked && !squadOptIn) {
      showError('Turn on squad streaming first', 'Flip the Squad streaming switch above to link into the squad.');
      return;
    }
    setBusy(true);
    try {
      await httpsCallable(functions, 'setMySquadLink')({
        teamStreamId: squad.teamStreamId, linked: !squad.myLinked,
      });
      fetchSquadState();
      notifySquadLinkChanged();
    } catch (err) {
      showError('Could not update your squad link', squadErr(err, 'Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  // Squad MASTER switch (game-party model): OFF = dormant, hidden from
  // browse, no squad Super Crits. Captain only (server-enforced).
  const toggleSquadLink = async () => {
    if (busy || !squad) return;
    setBusy(true);
    try {
      await httpsCallable(functions, 'setTeamStreamLink')({
        teamStreamId: squad.teamStreamId, active: squad.linkActive === false,
      });
      fetchSquadState();
      if (onSquadsChanged) onSquadsChanged();
    } catch (err) {
      showError('Could not toggle the squad link', squadErr(err, 'Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  // End is a two-tap confirm (first tap arms the button) - this codebase
  // never uses browser confirm() dialogs. Ends squad STREAMING only; the
  // team itself is untouched.
  const endSquad = async () => {
    if (!squad) return;
    if (!confirmEnd) { setConfirmEnd(true); return; }
    if (busy) return;
    setBusy(true);
    try {
      await httpsCallable(functions, 'endTeamStream')({ teamStreamId: squad.teamStreamId });
      setConfirmEnd(false);
      fetchSquadState();
      if (onSquadsChanged) onSquadsChanged();
    } catch (err) {
      showError('Could not end the squad stream', squadErr(err, 'Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <ActivityIndicator color={COLORS.GOLD} style={{ marginVertical: 20 }} />;
  }

  const teamLabel = team ? `${team.name}${team.tag ? ` [${team.tag}]` : ''}` : '';

  return (
    <>
      {/* Account-level opt-in (founder 2026-07-21): starting squad streaming
          and linking into the split both require this switch ON. */}
      <View style={styles.squadRow}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={styles.squadRowName}>Squad streaming</Text>
          <Text style={styles.squadRowMeta}>
            {squadOptIn
              ? 'On - you can squad up with your team'
              : 'Off - turn it on to squad up with your team'}
          </Text>
        </View>
        <TouchableOpacity
          style={[styles.squadRowBtn, squadOptIn && styles.squadRowBtnGold]}
          onPress={toggleSquadOptIn}
        >
          <Text style={squadOptIn ? styles.squadRowBtnGoldText : styles.squadRowBtnText}>
            {squadOptIn ? 'On' : 'Off'}
          </Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.fieldLabel}>Your team squad</Text>

      {!team ? (
        <>
          {/* Squads are powered by the registered Team - no team, no squad. */}
          <Text style={styles.squadEmptyText}>
            Squad streams are powered by your Team - your teammates are your
            squad. Create or join a team first.
          </Text>
          {!hideTeamLink ? (
            <TouchableOpacity style={[styles.modalConfirmBtn, { marginTop: 10 }]} onPress={goToYourTeam}>
              <Text style={styles.modalConfirmText}>Open Your Team</Text>
            </TouchableOpacity>
          ) : null}
        </>
      ) : !squad ? (
        <>
          <View style={styles.squadRow}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.squadRowName} numberOfLines={1}>{teamLabel}</Text>
              <Text style={styles.squadRowMeta}>
                {team.memberCount} member{team.memberCount === 1 ? '' : 's'}
                {'  |  squad streaming off'}
              </Text>
            </View>
            {team.isCaptain ? (
              <TouchableOpacity
                style={[styles.squadRowBtn, styles.squadRowBtnGold, busy && { opacity: 0.6 }]}
                onPress={startSquadStreaming}
                disabled={busy}
              >
                <Text style={styles.squadRowBtnGoldText}>
                  {busy ? 'Starting...' : 'Start squad streaming'}
                </Text>
              </TouchableOpacity>
            ) : null}
          </View>
          {!team.isCaptain ? (
            <Text style={styles.squadEmptyText}>
              Your team captain starts squad streaming for the whole team.
            </Text>
          ) : null}
          {team.memberCount < 2 ? (
            <Text style={styles.squadEmptyText}>
              {'Squad streams take at least 2 teammates - share your team invite code'}
              {hideTeamLink ? ' above.' : ' from Your Team.'}
            </Text>
          ) : null}
        </>
      ) : (
        <>
          <View style={styles.squadRow}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.squadRowName} numberOfLines={1}>{teamLabel}</Text>
              <Text style={styles.squadRowMeta}>
                {squad.memberCount} member{squad.memberCount === 1 ? '' : 's'}
                {`  |  ${squad.linkedCount || 0} linked`}
                {squad.linkActive === false ? '  |  squad off' : ''}
                {team.isCaptain ? '  |  you are the captain' : ''}
              </Text>
            </View>
            {/* MY link - per-member, default off; gold = you're linked.
                Linking needs the master switch ON, but UNLINKING is always
                available - a linked member must never be stranded because
                the captain flipped the squad off. */}
            {(squad.linkActive !== false || squad.myLinked) ? (
              <TouchableOpacity
                style={[styles.squadRowBtn, squad.myLinked && styles.squadRowBtnGold]}
                onPress={toggleMyLink}
                disabled={busy}
              >
                <Text style={squad.myLinked ? styles.squadRowBtnGoldText : styles.squadRowBtnText}>
                  {squad.myLinked ? 'Linked' : 'Link up'}
                </Text>
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={styles.squadRowBtn}
              onPress={() => onNavigate && onNavigate(squad.teamStreamId)}
            >
              <Text style={styles.squadRowBtnText}>Open</Text>
            </TouchableOpacity>
            {team.isCaptain ? (
              <>
                <TouchableOpacity
                  style={styles.squadRowBtn}
                  onPress={toggleSquadLink}
                  disabled={busy}
                >
                  <Text style={[styles.squadRowBtnText, squad.linkActive !== false && { color: COLORS.GOLD }]}>
                    {squad.linkActive !== false ? 'Squad on' : 'Squad off'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.squadRowBtn}
                  onPress={endSquad}
                  disabled={busy}
                >
                  <Text style={[styles.squadRowBtnText, confirmEnd && { color: '#e05555' }]}>
                    {confirmEnd ? 'Confirm' : 'End'}
                  </Text>
                </TouchableOpacity>
              </>
            ) : null}
          </View>

          {/* Roster is READ-ONLY here - membership is the team roster and is
              managed from the Your Team tab (founder 2026-08-04). */}
          {roster.length > 0 ? (
            <View style={styles.squadRosterBox}>
              {roster.map((m) => (
                <View key={m.uid} style={styles.squadRosterRow}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <Ionicons
                      name={m.isCaptain ? 'shield' : 'person'}
                      size={12}
                      color={m.isCaptain ? COLORS.GOLD : '#666'}
                      style={{ marginRight: 6 }}
                    />
                    <Text style={styles.squadRowMeta} numberOfLines={1}>
                      {m.userName}
                    </Text>
                  </View>
                  {m.live ? <Text style={styles.rosterLiveChip}>LIVE</Text> : null}
                  {m.linked ? <Text style={styles.rosterLinkedChip}>linked</Text> : null}
                </View>
              ))}
            </View>
          ) : null}
        </>
      )}

      {/* Roster management pointer - squad membership IS the team roster. */}
      {team && !hideTeamLink ? (
        <TouchableOpacity onPress={goToYourTeam}>
          <Text style={styles.teamLinkText}>
            Your squad is your team roster - manage it in Your Team
          </Text>
        </TouchableOpacity>
      ) : null}
    </>
  );
}

// ----------------------------------------------------------------------------
// SquadLinkPrompt - the solo-or-squad detection modal (founder 2026-08-04).
// Fires only while the signed-in user is STREAMING and their team's squad is
// active. Two modes from getSquadLinkPrompt:
//   'link'       - a teammate is live but you're NOT linked: "squad up?"
//   'solo_check' - you're LINKED but streaming alone: "streaming solo?"
//                  (offer to unlink so the personal stream stays personal)
// Each mode prompts at most once per session; answering the solo check does
// NOT suppress a later link prompt (the situations are different - e.g. you
// unlink for a solo run, then the crew shows up an hour later). Self-gating:
// renders nothing when the feature flag is off, signed out, or there is
// nothing to suggest - safe to mount unconditionally on streamer-facing
// screens (/streams, Streaming Hub, Your Team).
// ----------------------------------------------------------------------------
const promptHandledModes = {}; // { link: true, solo_check: true } per session

export function SquadLinkPrompt() {
  const { showError } = useError();
  const functions = getFunctions();
  const teamVisible = useTeamStreamsVisible();
  // Stack navigation keeps background screens MOUNTED, so this component can
  // be alive on /streams, the Streaming Hub, and Your Team at once - only
  // the focused screen's instance may poll or render, or the user gets
  // stacked modals.
  const isFocused = useIsFocused();
  const [prompt, setPrompt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isFocused || !teamVisible) return undefined;
    if (!getAuth().currentUser) return undefined;
    // Both modes consumed = nothing left to show this session; stop paying
    // for the poll (the callable runs several Firestore reads server-side).
    if (promptHandledModes.link && promptHandledModes.solo_check) return undefined;
    let cancelled = false;
    const check = async () => {
      if (cancelled) return;
      if (promptHandledModes.link && promptHandledModes.solo_check) return;
      try {
        const fn = httpsCallable(functions, 'getSquadLinkPrompt');
        // supportsModes: pre-mode bundles rendered ANY prompt:true as the
        // link modal - the backend only emits solo_check to clients that
        // declare they can render it.
        const result = await fn({ supportsModes: true });
        const d = result.data || {};
        const mode = d.mode || 'link';
        if (cancelled) return;
        setPrompt((prev) => {
          if (!prev) {
            return (d.prompt && !promptHandledModes[mode]) ? { ...d, mode } : null;
          }
          // A modal is on screen: keep it ONLY while the server still agrees
          // with its mode. A linked-solo modal must not survive the crew
          // going live (stale "nobody's live" copy would nudge a wrong
          // unlink) - close it; a later poll may open the now-correct mode.
          return (d.prompt && mode === prev.mode) ? prev : null;
        });
      } catch (_) { /* the prompt is best-effort - never toast for it */ }
    };
    check();
    const poll = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(poll); };
  }, [isFocused, teamVisible, functions]);

  // An unfocused instance never renders its modal; if the user answered on
  // another screen, drop any stale local prompt instead of re-showing it.
  if (prompt && promptHandledModes[prompt.mode]) {
    setPrompt(null);
    return null;
  }
  if (!prompt || !isFocused) return null;

  const solo = prompt.mode === 'solo_check';
  const mates = Array.isArray(prompt.liveSquadmates) ? prompt.liveSquadmates : [];
  const extra = Math.max(0, (prompt.liveSquadmateCount || mates.length) - mates.length);
  const mateLine = mates.join(', ') + (extra > 0 ? ` and ${extra} more` : '');

  const setLink = async (linked) => {
    if (busy) return;
    setBusy(true);
    try {
      // Answer-time revalidation for the UNLINK path: the modal may have sat
      // open while teammates went live - never act on a stale "nobody's
      // live" assertion. (The link path needs no guard: linking while
      // together is always what the user wants.)
      if (!linked) {
        try {
          const fresh = await httpsCallable(functions, 'getSquadLinkPrompt')({ supportsModes: true });
          const f = fresh.data || {};
          if (!(f.prompt && f.mode === 'solo_check')) {
            // State changed under the modal - close WITHOUT consuming the
            // mode so the now-correct prompt can still appear.
            setPrompt(null);
            return;
          }
        } catch (_) { /* revalidation is best-effort; proceed */ }
      }
      await httpsCallable(functions, 'setMySquadLink')({
        teamStreamId: prompt.teamStreamId, linked,
      });
      promptHandledModes[prompt.mode] = true;
      setPrompt(null);
      notifySquadLinkChanged();
    } catch (err) {
      showError(
        linked ? 'Could not link you up' : 'Could not unlink you',
        String(err?.message || '').replace(/^.*?:/, '').trim() || 'Please try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  // Backdrop/Esc dismiss is busy-gated like the buttons: a dismiss racing an
  // in-flight answer must not consume the mode before the answer resolves.
  const dismiss = () => {
    if (busy) return;
    promptHandledModes[prompt.mode] = true;
    setPrompt(null);
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={dismiss}>
      <Pressable style={styles.promptBackdrop} onPress={dismiss}>
        <Pressable style={styles.promptCard} onPress={(e) => e.stopPropagation()}>
          {solo ? (
            <>
              <Text style={styles.promptTitle}>Streaming solo?</Text>
              <Text style={styles.promptBody}>
                You're linked to {'"'}{prompt.name}{'"'}, but none of your
                teammates are live right now. If this one's a solo stream,
                unlink so it stays all yours. You can link back up any time
                the crew goes live.
              </Text>
              <View style={styles.promptBtnRow}>
                <TouchableOpacity
                  style={[styles.promptBtn, styles.promptBtnGold, busy && { opacity: 0.6 }]}
                  onPress={() => setLink(false)}
                  disabled={busy}
                >
                  <Text style={styles.promptBtnGoldText}>{busy ? 'Unlinking...' : "I'm solo - unlink"}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.promptBtn} onPress={dismiss} disabled={busy}>
                  <Text style={styles.promptBtnText}>My squad's joining</Text>
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <>
              <Text style={styles.promptTitle}>Squad up?</Text>
              <Text style={styles.promptBody}>
                We detected you and your teammates are streaming - {mateLine}
                {mates.length === 1 && extra === 0 ? ' is' : ' are'} live in
                {' "'}{prompt.name}{'". '}
                Link up so the whole crew shares the support?
              </Text>
              <View style={styles.promptBtnRow}>
                <TouchableOpacity
                  style={[styles.promptBtn, styles.promptBtnGold, busy && { opacity: 0.6 }]}
                  onPress={() => setLink(true)}
                  disabled={busy}
                >
                  <Text style={styles.promptBtnGoldText}>{busy ? 'Linking...' : "We're together - link up"}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.promptBtn} onPress={dismiss} disabled={busy}>
                  <Text style={styles.promptBtnText}>I'm streaming solo</Text>
                </TouchableOpacity>
              </View>
            </>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// Styles copied verbatim from StreamsScreen so the panel renders identically
// inside the squad modal, the Streaming Hub section, and the Your Team card.
const styles = StyleSheet.create({
  fieldLabel: { color: '#aaa', fontSize: 12, fontWeight: '700', marginBottom: 6, marginTop: 10 },
  modalConfirmBtn: { flex: 1, backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 11, alignItems: 'center' },
  modalConfirmText: { color: '#0b0b0b', fontSize: 13, fontWeight: '800' },

  squadRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, paddingVertical: 9, paddingHorizontal: 10, marginBottom: 8,
  },
  squadRowName: { color: '#eee', fontSize: 13, fontWeight: '700' },
  squadRowMeta: { color: '#777', fontSize: 11, marginTop: 1 },
  squadRowBtn: {
    borderColor: '#333', borderWidth: 1, borderRadius: 6,
    paddingVertical: 6, paddingHorizontal: 10,
  },
  squadRowBtnText: { color: '#aaa', fontSize: 11.5, fontWeight: '700' },
  squadRowBtnGold: { backgroundColor: COLORS.GOLD, borderColor: COLORS.GOLD },
  squadRowBtnGoldText: { color: '#0b0b0b', fontSize: 11.5, fontWeight: '800' },
  squadEmptyText: { color: '#777', fontSize: 12, marginVertical: 6 },
  squadRosterBox: {
    marginLeft: 10, marginTop: -4, marginBottom: 6, paddingLeft: 10,
    borderLeftColor: '#222', borderLeftWidth: 1,
  },
  squadRosterRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 3, gap: 10,
  },
  rosterLiveChip: { color: COLORS.LIVE, fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },
  rosterLinkedChip: { color: COLORS.GOLD, fontSize: 10, fontWeight: '700' },
  teamLinkText: { color: COLORS.GOLD, fontSize: 12, fontWeight: '600', marginTop: 6, marginBottom: 4 },

  promptBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.72)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  promptCard: {
    width: '100%', maxWidth: 380, backgroundColor: '#141414',
    borderColor: '#2a2a2a', borderWidth: 1, borderRadius: 12, padding: 18,
  },
  promptTitle: { color: COLORS.GOLD, fontSize: 16, fontWeight: '800', marginBottom: 8 },
  promptBody: { color: '#ddd', fontSize: 13, lineHeight: 19 },
  promptBtnRow: { flexDirection: 'row', gap: 10, marginTop: 16 },
  promptBtn: {
    flex: 1, borderColor: '#333', borderWidth: 1, borderRadius: 8,
    paddingVertical: 10, alignItems: 'center',
  },
  promptBtnText: { color: '#aaa', fontSize: 13, fontWeight: '700' },
  promptBtnGold: { backgroundColor: COLORS.GOLD, borderColor: COLORS.GOLD },
  promptBtnGoldText: { color: '#0b0b0b', fontSize: 13, fontWeight: '800' },
});
